import { defineStore } from 'pinia';
import { apis } from '/@/apis';
import to from 'await-to-js';
import { MATERIAL_ABILITY } from '/@/enums/common-editor';
import { CaseType } from '/@/views/batch/utils/case-center';
import { adaptDefaultCaseTypeOptions } from '/@/views/batch/utils/category-adapt';
import { SHOP_PLATFORM_DISPLAY_MAP } from '/@/views/batch/components/shelf-edit-template-modal/enum';

export type IDictListItem = {
  label: string;
  value: string;
};

export type IIccProfile = {
  profile_id: string;
  standard_name: string;
  color_space: string;
  standard_organization: string;
  usage_stage: string;
  icc_file_reference: string;
  production_note: string;
};

interface IDictStore {
  creationSubjects: IDictListItem[];
  creationTechniques: IDictListItem[];
  creationScenarios: IDictListItem[];
  goodsBgChannels: IDictListItem[];
  editorConfig: any;
  promptHelperConfig: any;
  goodsBgTypes: any;
  goodsBgCategories: any;
  modelBgCategories: any;
  draftStyles: any;
  dials: any;
  threeDdials: any;
  dialCategories: any;
  threeDdialCategories: any;
  goodsBgCategoriesMap: {
    [k in string]: any;
  };
  modelFaceFilters: {
    label: string;
    value: string;
    children: IDictListItem[];
  }[];
  goodTemplateTypes: {
    label: string;
    value: string;
    color: string;
  }[];
  shelfTemplateTypes: {
    label: string;
    value: string;
    color?: string;
    disabled?: boolean;
  }[];
  redrawStyles: any[];
  to3DStyles: any[];
  videoStyles: any[];
  videoGoodsStyles: any[];
  /** 主图视频进阶效果预设 main-image-video-style-prompt */
  mainImageVideoStylePrompts: any[];
  polyptychPromptConfig: any[];
  polyptychDoubleSidePromptConfig: any[];
  videoStylePrompts: any[];
  batchHelps: any;
  textOneWhiteList: string[];
  defaultCaseType: any[];

  caseTypeByCategory: Record<
    CaseType,
    {
      label: string;
      value: string;
    }[]
  >;
  dashboardHolidays: {
    preview: string;
    redirect: string;
    id: string;
  }[];
  casesBanner: {
    imageUrl: string;
    backgroundColor: string;
    redirectUrl: string;
  }[];
  ironStyleOptions: {
    label: string;
    value: string;
    preview: string;
  }[];
  recommendTool: any;
  tkVideoLanguages: {
    label: string;
    value: string;
  }[];
  iccProfiles: IIccProfile[];
  omniPatternExtractions: {
    id: number;
    name: string;
    code: string;
    applicableScene: string;
    ar: string;
    imageRatios: string;
    prompt: string;
    modelName: string;
  }[];

  recreationTypes: Array<{
    name: string;
    code: string;
    exampleImage: string;
    prompt: string;
  }>;
  defaultCaseTags: any;
  batchWatermarkWhitelist: string[];
  promptWordsWhitelist: string[];
  tkVideoCustomPrompt: { label: string; prompts: string[] }[];
  batchVideoTransitions: { label: string; value: string; url?: string }[];
  videoPublishOrgIdWhiteList: string[];
  videoPublishOrgIdWhiteListLoaded: boolean;
  videoPublishOrgIdWhiteListLoading: boolean;
}

const dicts = apis.dict;
const { editorCommon } = apis;

export const dictStore = defineStore({
  id: 'dict',
  state: (): IDictStore => ({
    creationSubjects: [],
    creationTechniques: [],
    creationScenarios: [],
    goodsBgTypes: [],
    editorConfig: null,
    goodsBgCategories: null,
    goodsBgCategoriesMap: {},
    modelBgCategories: null,
    modelFaceFilters: [],
    goodsBgChannels: [],
    goodTemplateTypes: [],
    draftStyles: {},
    promptHelperConfig: null,
    dials: [],
    dialCategories: [],
    threeDdials: [],
    threeDdialCategories: [],
    redrawStyles: [],
    to3DStyles: [],
    videoStyles: [],
    videoGoodsStyles: [],
    mainImageVideoStylePrompts: [],
    shelfTemplateTypes: [],
    polyptychPromptConfig: [],
    polyptychDoubleSidePromptConfig: [],
    videoStylePrompts: [],
    batchHelps: {},
    textOneWhiteList: [],
    caseTypeByCategory: {},
    defaultCaseType: [],
    dashboardHolidays: [],
    casesBanner: [],
    ironStyleOptions: [],
    tkVideoLanguages: [],
    iccProfiles: [],
    omniPatternExtractions: [],
    recreationTypes: [],
    recommendTool: [],
    defaultCaseTags: [],
    batchWatermarkWhitelist: [],
    tkVideoCustomPrompt: [],
    batchVideoTransitions: [],
    videoPublishOrgIdWhiteList: [],
    videoPublishOrgIdWhiteListLoaded: false,
    videoPublishOrgIdWhiteListLoading: false,
  }),
  getters: {
    isVideoPublishMenuVisible: (state) => (orgId?: string | number) => {
      if (!state.videoPublishOrgIdWhiteListLoaded) return false;
      if (!state.videoPublishOrgIdWhiteList.length) return true;
      return state.videoPublishOrgIdWhiteList.includes(String(orgId || ''));
    },
  },
  actions: {
    async getCreationSubjects() {
      if (!this.creationSubjects || this.creationSubjects.length < 1) {
        const data = await dicts.getDict('aiad', 'work-subjects');
        if (data) {
          this.creationSubjects = JSON.parse(data);
        }
      }
      return this.creationSubjects;
    },

    async getGoodTemplateTypes() {
      if (!this.goodTemplateTypes.length) {
        const result = await apis.batch.getGoodTemplateTypes();
        this.goodTemplateTypes = result.map((v) => ({
          label: v.text,
          value: v.key,
          color: v.color || 'orange',
        }));
      }
      return this.goodTemplateTypes;
    },

    async getShelfTemplateTypes() {
      if (!this.shelfTemplateTypes.length) {
        const result = await apis.batch.getShopPlatforms();
        this.shelfTemplateTypes = [
          ...result.map((v) => {
            const display = SHOP_PLATFORM_DISPLAY_MAP[v.key];
            return {
              label: display?.label || v.text,
              value: v.key,
              desc: display?.desc,
            };
          }),
        ];

        if (!this.shelfTemplateTypes.some((v) => v.value === 'temu')) {
          this.shelfTemplateTypes.push({
            label: 'Temu [开发中]',
            value: 'temu',
            disabled: true,
          });
        }
      }
      return this.shelfTemplateTypes;
    },

    async getCreationTechniques() {
      if (!this.creationTechniques || this.creationTechniques.length < 1) {
        const data = await dicts.getDict('aiad', 'work-techniques');
        if (data) {
          this.creationTechniques = JSON.parse(data);
        }
      }
      return this.creationTechniques;
    },

    async getCreationScenarios() {
      if (!this.creationScenarios || this.creationScenarios.length < 1) {
        const data = await dicts.getDict('aiad', 'work-scenarios');
        if (data) {
          this.creationScenarios = JSON.parse(data);
        }
      }
      return this.creationScenarios;
    },

    async getEditorConfig() {
      if (!this.editorConfig) {
        const [err, data] = await to(dicts.getDict('aiad', 'editor-config'));
        if (!err && data) {
          this.editorConfig = data;
        }
      }
      return this.editorConfig;
    },

    async getPromptHelperConfig() {
      if (!this.promptHelperConfig) {
        const [err, data] = await to(dicts.getDict('aiad', 'prompt-assistant-options'));
        if (!err && data) {
          this.promptHelperConfig = data;
        }
      }
      return this.promptHelperConfig;
    },

    async getOmniPatternExtractions() {
      if (!this.omniPatternExtractions.length) {
        this.omniPatternExtractions = await apis.batch.getOmniPatternExtractions();
      }
      return this.omniPatternExtractions;
    },

    async getWorkflowHotTemplates() {
      const [err, data] = await to(dicts.getDict('aiad', 'workflow-hot-templates'));
      return JSON.parse(data || '[]');
    },

    async getDials() {
      if (!this.dials?.length) {
        const [err, data] = await to(apis.batch.getDials());
        if (!err && data) {
          this.dials = data;
        }
      }
      return this.dials;
    },
    async get3DDials() {
      if (!this.threeDdials?.length) {
        const [err, data] = await to(apis.batch.get3DDials());
        if (!err && data) {
          this.threeDdials = data;
        }
      }
      return this.dials;
    },

    async getIronStyleOptions() {
      if (!this.ironStyleOptions?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'iron-style-options'));
        if (!err && data) {
          this.ironStyleOptions = JSON.parse(data) as any;
        }
      }
      return this.ironStyleOptions;
    },

    async getTkVideoLanguages() {
      if (!this.tkVideoLanguages?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'tk-video-languages'));
        if (!err && data) {
          this.tkVideoLanguages = JSON.parse(data) as any;
        }
      }
      return this.tkVideoLanguages;
    },

    async getRecommendTool() {
      const loaded =
        this.recommendTool &&
        !Array.isArray(this.recommendTool) &&
        Object.keys(this.recommendTool).length > 0;
      if (!loaded) {
        const [err, data] = await to(dicts.getDict('aiad', 'recommend-tool'));
        if (!err && data) {
          this.recommendTool = data as any;
        }
      }
      return this.recommendTool;
    },

    async getDefaultCaseTags() {
      if (!this.defaultCaseTags?.length) {
        const [err, data] = await to(apis.batch.getDefaultCaseTags());
        if (!err && data) {
          this.defaultCaseTags = data as any;
        }
      }
      return this.defaultCaseTags;
    },

    async getRedrawStyles() {
      if (!this.redrawStyles?.length) {
        const [err, data] = await to(
          apis.batch.getRedrawStyles({
            pageSize: 9999,
          })
        );
        if (!err && data) {
          this.redrawStyles = data.items;
        }
      }
      return this.redrawStyles;
    },

    async getTo3DStyles() {
      if (!this.to3DStyles?.length) {
        const [err, data] = await to(
          apis.batch.getRedrawStyles({
            pageSize: 999,
            type: 'pod',
          })
        );
        if (!err && data) {
          this.to3DStyles = data.items;
        }
      }
      return this.to3DStyles;
    },

    async getTextOneWhiteList() {
      if (!this.textOneWhiteList?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'text-one-white-list'));
        if (!err && data) {
          this.textOneWhiteList = JSON.parse(data);
        }
      }
      return this.textOneWhiteList;
    },

    async getDashboardHolidays() {
      if (!this.dashboardHolidays?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'batch-dashboard-holidays'));
        if (!err && data) {
          this.dashboardHolidays = JSON.parse(data);
        }
      }
      return this.dashboardHolidays;
    },

    async getCasesBanner() {
      if (!this.casesBanner.length) {
        const [err, data] = await to(apis.batch.getCasesBanners());
        if (!err && data) {
          this.casesBanner = data as any;
        }
      }
      return this.casesBanner;
    },

    async getVideoStyles() {
      if (!this.videoStyles?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'batch-video-prompts'));
        if (!err && data) {
          this.videoStyles = JSON.parse(data);
        }
      }
      return this.videoStyles;
    },

    async getCaseTypeByCategory() {
      if (!Object.keys(this.caseTypeByCategory)?.length) {
        const [err, data] = await to(apis.batch.getCaseType());
        if (!err && data) {
          this.caseTypeByCategory = data;
        }
      }

      return this.caseTypeByCategory;
    },

    async getDefaultCaseType() {
      if (!this.defaultCaseType?.length) {
        const [err, data] = await to(apis.batch.getDefaultCaseType());
        if (!err && data) {
          this.defaultCaseType = adaptDefaultCaseTypeOptions(data);
        }
      }

      return this.defaultCaseType;
    },

    async getVideoGoodsStyles() {
      if (!this.videoGoodsStyles?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'batch-video-goods-prompts'));
        if (!err && data) {
          this.videoGoodsStyles = JSON.parse(data);
        }
      }
      return this.videoGoodsStyles;
    },

    async getMainImageVideoStylePrompts() {
      if (!this.mainImageVideoStylePrompts?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'main-image-video-style-prompt'));
        if (!err && data) {
          const list = typeof data === 'string' ? JSON.parse(data) : data;
          this.mainImageVideoStylePrompts = (list || []).map((item: any) => ({
            ...item,
            name: item.label,
            prompt: item.value,
          }));
        }
      }
      return this.mainImageVideoStylePrompts;
    },

    async getDialCategories() {
      if (!this.dialCategories?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'dial-types'));
        if (!err && data) {
          this.dialCategories = JSON.parse(data);
        }
      }
      return this.dialCategories;
    },
    async get3DDialCategories() {
      if (!this.threeDdialCategories?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'dial-three-types'));
        if (!err && data) {
          this.threeDdialCategories = JSON.parse(data);
        }
      }
      return this.threeDdialCategories;
    },

    async getPodExtension() {
      const [err, data] = await to(dicts.getDict('aiad', 'pod-extension'));
      if (!err && data) {
        const { url } = data;
        return url;
      }
    },

    // 获取商品背景图品类
    async getGoodsBgTypes() {
      if (!this.goodsBgTypes?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'goods-bg-types'));
        if (!err && data) {
          this.goodsBgTypes = JSON.parse(data);
          if (this.goodsBgTypes.length) {
            this.goodsBgTypes.unshift({
              label: '全部品类',
              value: '',
            });
          }
        }
      }
      return this.goodsBgTypes;
    },

    // 商品图换背景，背景类别
    async getGoodsBgCategories() {
      if (!this.goodsBgCategories?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'goods-bg-categories'));
        if (!err && data) {
          this.goodsBgCategories = JSON.parse(data).map((v) => ({
            ...v,
            children: [],
          }));
          this.goodsBgCategories.unshift({
            label: '推荐',
            value: '',
            children: [
              // {
              //   id: null,
              //   name: '随机背景',
              //   preview:
              //     'https://static.lingvisions.com/public/b6bbb6611d94485b800c7963f846af2e.png',
              // },
            ],
          });
        }
        const bgs = await editorCommon.goodsBgs();

        bgs.forEach((item) => {
          this.goodsBgCategories.forEach((parent) => {
            if ((item.recommend && !parent.value) || item.category === parent.value) {
              parent.children.push(item);
            }
          });
        });
      }

      return this.goodsBgCategories;
    },

    // 商品图换背景，背景类别
    async getGoodsBgCategoriesByFilterType(filterType: string) {
      if (!this.goodsBgCategoriesMap?.[filterType]?.length) {
        let bgs = await editorCommon.goodsBgs();
        bgs = bgs.filter((v) => !filterType || v.type === filterType);

        const [err, data] = await to(dicts.getDict('ops', 'goods-bg-categories'));
        if (!err && data) {
          const categories = JSON.parse(data).map((v) => ({
            ...v,
            children: [],
          }));
          categories.unshift({
            label: '推荐',
            value: '',
            children: [],
          });

          for (let i = 0; i < categories.length; i++) {
            const category = categories[i];
            if (!category.value) {
              bgs.forEach((bg) => {
                if (bg.recommend) {
                  category.children.push(bg);
                }
              });
            } else {
              bgs.forEach((bg) => {
                if (bg.category === category.value) {
                  category.children.push(bg);
                }
              });
            }

            if (!category.children.length) {
              categories.splice(i, 1);
              i--;
            }
          }

          this.goodsBgCategoriesMap[filterType] = categories;
        }
      }

      return this.goodsBgCategoriesMap?.[filterType];
    },

    async getDraftStyles(type) {
      if (!this.draftStyles?.[type]) {
        this.draftStyles[type] = await editorCommon.draftStyles({
          type,
        });
      }

      return this.draftStyles[type];
    },

    // ai换模特，背景类别
    async getModelBgCategories() {
      if (!this.modelBgCategories?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'model-bg-categories'));
        if (!err && data) {
          this.modelBgCategories = JSON.parse(data).map((v) => ({
            ...v,
            children: [],
          }));
          this.modelBgCategories.unshift({
            label: '推荐',
            value: '',
            children: [
              // {
              //   id: null,
              //   name: '原场景',
              //   preview:
              //     'https://static.lingvisions.com/public/b6bbb6611d94485b800c7963f846af2e.png',
              // },
            ],
          });
        }
        const bgs = await editorCommon.modelBgs();

        bgs.forEach((item) => {
          this.modelBgCategories.forEach((parent) => {
            if ((item.recommend && !parent.value) || item.category === parent.value) {
              parent.children.push(item);
            }
          });
        });
      }

      return this.modelBgCategories;
    },

    async getModelFaceFilters() {
      if (!this.modelFaceFilters.length) {
        const [genders, skins, ages] = await Promise.all([
          dicts.getDict('ops', 'model-face-genders').then((v) => JSON.parse(v)),
          dicts.getDict('ops', 'model-face-skins').then((v) => JSON.parse(v)),
          dicts.getDict('ops', 'model-face-ages').then((v) => JSON.parse(v)),
        ]);

        this.modelFaceFilters = [
          {
            label: '性别',
            value: 'gender',
            children: genders,
          },
          {
            label: '肤色',
            value: 'skin',
            children: skins,
          },
          {
            label: '年龄',
            value: 'age',
            children: ages,
          },
        ];
      }

      return this.modelFaceFilters;
    },

    // 获取商品换背景频道
    async getGoodsBgChannels() {
      if (!this.goodsBgChannels?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'goods-bg-channels'));
        if (!err && data) {
          const result: IDictListItem[] = [];
          const goodsBgChannels = JSON.parse(data);
          const channels = await apis.inspirations.getReferenceChannels('common');
          channels.forEach((channel) => {
            const v = goodsBgChannels.find((v) => +v.value === +channel.id);
            if (v) {
              result.push(v);
            }
          });
          this.goodsBgChannels = result;
        }
      }

      return this.goodsBgChannels;
    },

    // 获取多联画配置
    async getPolyptychPromptConfig() {
      if (!this.polyptychPromptConfig?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'polyptych-prompt-config'));
        if (!err && data) {
          this.polyptychPromptConfig = JSON.parse(data);
        }
      }
      return this.polyptychPromptConfig;
    },

    async getVideoStylePrompts() {
      if (!this.videoStylePrompts?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'video-style-prompt'));
        if (!err && data) {
          this.videoStylePrompts = JSON.parse(data);
        }
      }
      return this.videoStylePrompts;
    },

    // 获取多联画双面印花配置
    async getPolyptychDoubleSidePromptConfig() {
      if (!this.polyptychDoubleSidePromptConfig?.length) {
        const [err, data] = await to(dicts.getDict('ops', 'doublesided-prompt-config'));
        if (!err && data) {
          this.polyptychDoubleSidePromptConfig = JSON.parse(data);
        }
      }
      return this.polyptychDoubleSidePromptConfig;
    },

    async getBatchHelps() {
      if (Object.keys(this.batchHelps).length) return this.batchHelps;
      this.batchHelps = {
        [MATERIAL_ABILITY.CRAWLING]:
          'https://lingtuipoddy.feishu.cn/wiki/YXcYwfBwRiyGJXkN9sOc96jZnje',
        [MATERIAL_ABILITY.DETECT]:
          'https://lingtuipoddy.feishu.cn/wiki/DQG5wDOjYiath3k3kvVcNXmJnkd',
        [MATERIAL_ABILITY.IMAGE_TO_PRINT]:
          'https://lingtuipoddy.feishu.cn/wiki/Xep1wxzAVi5dMxktjewciYN7nnc',
        [MATERIAL_ABILITY.IMAGE]: 'https://lingtuipoddy.feishu.cn/wiki/X7tmwrbq6ihstMkXnz5cgeZmnGc',
        [MATERIAL_ABILITY.PRINTING_MEME]:
          'https://lingtuipoddy.feishu.cn/wiki/ViHOwdWSGiuGUPkdLCKcqQ8mnxb',
        [MATERIAL_ABILITY.TEXT]: 'https://lingtuipoddy.feishu.cn/wiki/FoSfwv2DOicdbdkacB9c0o74n3e',
        [MATERIAL_ABILITY.PART_REPLACE]:
          'https://lingtuipoddy.feishu.cn/wiki/KJ0dwGVkIieT5xkJOuecw8PMnSe',
        [MATERIAL_ABILITY.SEAMLESS]:
          'https://lingtuipoddy.feishu.cn/wiki/BU7LwRnJeiq7GmkqRqHcxQnSnwe',
        [MATERIAL_ABILITY.TILING]:
          'https://lingtuipoddy.feishu.cn/wiki/FDXWwFZwOiTH6fkonHNcn6mfnUc',
        [MATERIAL_ABILITY.STYLE_REFERENCE]:
          'https://lingtuipoddy.feishu.cn/wiki/Xa9GwFOnAiCtt3kezssc2lXJnbe',
        [MATERIAL_ABILITY.REDRAW]:
          'https://lingtuipoddy.feishu.cn/wiki/VFyAwY1XriMJ6skOl2Zcb9l1nLd',
        [MATERIAL_ABILITY.TO3D]: 'https://lingtuipoddy.feishu.cn/wiki/TO3D',
        [MATERIAL_ABILITY.DIY_VECTOR_COMPOSITING]:
          'https://lingtuipoddy.feishu.cn/wiki/NdNrwqiS7iuTspkW1sOccJion6f',
        [MATERIAL_ABILITY.UV_LAYERING]:
          'https://lingtuipoddy.feishu.cn/wiki/Sq0AwzoEsidrhUkMEkXcLZdunbh',
        [MATERIAL_ABILITY.PRINTING_COMPOSITING]:
          'https://lingtuipoddy.feishu.cn/wiki/Ub0nwYCP0iCds1klmpycacG2nbh',
        [MATERIAL_ABILITY.MATTING]:
          'https://lingtuipoddy.feishu.cn/wiki/UL3ywHKuWiwfU7kAc2DcwZStnke',
        [MATERIAL_ABILITY.UPSCALE]:
          'https://lingtuipoddy.feishu.cn/wiki/VIvhwTsapijLOikGB2Sc17f4nag',
        [MATERIAL_ABILITY.WATERMARK]:
          'https://lingtuipoddy.feishu.cn/wiki/SBNlwtCmuiIjwDk0UDMcfxZQnCg',
        [MATERIAL_ABILITY.EXPAND]:
          'https://lingtuipoddy.feishu.cn/wiki/SBNlwtCmuiIjwDk0UDMcfxZQnCg',
        [MATERIAL_ABILITY.SVG]: 'https://lingtuipoddy.feishu.cn/wiki/OaeVwaRe8iJnejkI1vUcYAPgnvc',
        [MATERIAL_ABILITY.MULTI_SIZE]:
          'https://lingtuipoddy.feishu.cn/wiki/K1xwwhWUbiHZi2knEsucauIjnBU',
        [MATERIAL_ABILITY.MOCKUP]:
          'https://lingtuipoddy.feishu.cn/wiki/OxjNwtyrii6JkWkQASTcctsfnXc',
        [MATERIAL_ABILITY.TITLE]: 'https://lingtuipoddy.feishu.cn/wiki/ScnAwIri6i5FfAk0whdc7Hytnsb',
        [MATERIAL_ABILITY.TORT]: 'https://lingtuipoddy.feishu.cn/wiki/Hr8dw06wSifn9Ik2Rp6cv2b8njY',
        [MATERIAL_ABILITY.VIDEO]: 'https://lingtuipoddy.feishu.cn/wiki/BMl2wDL7viSU2GkqQbpcr66cnUh',
        [MATERIAL_ABILITY.CUT_PIECES]:
          'https://lingtuipoddy.feishu.cn/wiki/FMVjwcdO5it7S3kyRMDcw8vxnTe',
        [MATERIAL_ABILITY.BLEND]: 'https://lingtuipoddy.feishu.cn/wiki/Enbsw1GZKiUYtLkDIVZcwd7Gn9c',
        [MATERIAL_ABILITY.BATCH_WATERMARK]:
          'https://lingtuipoddy.feishu.cn/wiki/Ids0wHZpliIE3QkhHrLcERhkntc',
        [MATERIAL_ABILITY.BATCH_EXPAND]:
          'https://lingtuipoddy.feishu.cn/wiki/FEDxweivGisOBakOcHAcnHbNnQd',
        [MATERIAL_ABILITY.WORKFLOW]:
          'https://lingtuipoddy.feishu.cn/wiki/L9ZVw2HxuiXwFVk0VZOcXxm8nNh',
        [MATERIAL_ABILITY.POLYPTYCH]:
          'https://lingtuipoddy.feishu.cn/wiki/Evfywt0SOiH2e4kvfG4cgLB7np1',
        [MATERIAL_ABILITY.BATCH_IMAGE_PROCESSING]:
          'https://lingtuipoddy.feishu.cn/wiki/L5YmwYwfVilzWfk0qZUc2daLnnh',
        [MATERIAL_ABILITY.DRESS]: 'https://lingtuipoddy.feishu.cn/wiki/QZ3nwddvSiAxDAke6T3cyesbngf',
        [MATERIAL_ABILITY.DIY_COMPOSITING]:
          'https://lingtuipoddy.feishu.cn/wiki/IoyQwuDhpifhc2kYub5cIGXtnTf',
        [MATERIAL_ABILITY.DIY_FULFILLMENT]:
          'https://lingtuipoddy.feishu.cn/wiki/IoyQwuDhpifhc2kYub5cIGXtnTf',
        [MATERIAL_ABILITY.PET_DRESSUP]:
          'https://lingtuipoddy.feishu.cn/wiki/Aa7pwR956ibDHrkyUkYcEBMynUf',
        [MATERIAL_ABILITY.PRODUCT_FISSION]:
          'https://lingtuipoddy.feishu.cn/wiki/FmhnwTQVGin0d2kLlj5cRBhAnyc',
        [MATERIAL_ABILITY.SMART_MOCKUP]:
          'https://lingtuipoddy.feishu.cn/wiki/PwaywLUsYi02L0kAYiXct4AWnkA',
        [MATERIAL_ABILITY.CHANGE_BACKGROUND_NEW]:
          'https://lingtuipoddy.feishu.cn/wiki/GfOtwyfWPi0oltkaAjtcasEFn1b',
      };
    },

    async getRecreationTypes() {
      if (!this.recreationTypes?.length) {
        const [err, data] = await to(apis.batch.getImageRecreateConfigList());
        if (!err && data) {
          this.recreationTypes = data;
        }
      }
      console.log(this.recreationTypes);
      return this.recreationTypes;
    },

    async getBatchWatermarkWhitelist() {
      if (!this.batchWatermarkWhitelist?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'batch-watermark-whitelist'));
        if (!err && data) {
          this.batchWatermarkWhitelist = JSON.parse(data) as any;
        }
      }
      return this.batchWatermarkWhitelist;
    },

    async getPromptWordsWhitelist() {
      if (!this.promptWordsWhitelist?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'prompt-words-whitelist'));
        if (!err && data) {
          this.promptWordsWhitelist = JSON.parse(data) as any;
        }
      }
      return this.promptWordsWhitelist;
    },

    async getTkVideoCustomPrompt() {
      if (!this.tkVideoCustomPrompt?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'tk-video-custom-prompt'));
        if (!err && data) {
          this.tkVideoCustomPrompt = JSON.parse(data) as any;
        }
      }
      return this.tkVideoCustomPrompt;
    },

    async getIccProfiles() {
      if (!this.iccProfiles?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'icc'));
        if (!err && data) {
          this.iccProfiles = JSON.parse(data || '[]') as IIccProfile[];
        }
      }
      return this.iccProfiles;
    },

    // batch-video-transitions
    async getBatchVideoTransitions() {
      if (!this.batchVideoTransitions?.length) {
        const [err, data] = await to(dicts.getDict('aiad', 'batch-video-transitions'));
        if (!err && data) {
          this.batchVideoTransitions = JSON.parse(data) as any;
        }
      }
      return this.batchVideoTransitions;
    },

    async getVideoPublishOrgIdWhiteList() {
      if (this.videoPublishOrgIdWhiteListLoaded || this.videoPublishOrgIdWhiteListLoading) {
        return this.videoPublishOrgIdWhiteList;
      }
      this.videoPublishOrgIdWhiteListLoading = true;
      try {
        const [err, data] = await to(dicts.getDict('aiad', 'video-publish-org-id-white-list'));
        if (err) return this.videoPublishOrgIdWhiteList;

        const parsed = data ? JSON.parse(data) : [];
        if (parsed == null || Array.isArray(parsed)) {
          this.videoPublishOrgIdWhiteList = Array.isArray(parsed) ? parsed.map(String) : [];
          this.videoPublishOrgIdWhiteListLoaded = true;
        }
        return this.videoPublishOrgIdWhiteList;
      } finally {
        this.videoPublishOrgIdWhiteListLoading = false;
      }
    },
  },
});
